/**
 * Okra — Hexendoktor (Hexmaster). LMAO-Style (BATTLE-14):
 * "unheimlicher Gift-Schamane" — verdrehte, gerippte Schote (Rippen als 3D-Waelle
 * mit Licht-/Schatten-Kanten), Voodoo-Kappe mit Knochen- und Feder-Deko, subtiler
 * Gift-Schimmer in KEYWORD-Gift-Gruen (docs/12 §2 — er IST der Gift-Applier).
 * 2★: Tribal-Wickelband mit Kreuz-Stichen + Zahn-Trophaeen.
 * 3★: pulsierende Gift-Runen + drittes Auge (reagieren auf pose.castPulse).
 * Referenz-Qualitaet: kohlrabi.ts. Contract: species/types.ts.
 */
import type { PaintParams, SpeciesPainter } from './types'
import { darken, glow, lighten, rgba, rimLight, specular, volumeGradient } from '../draw3d'
import { KEYWORD_COLORS } from '../../ui/theme'

/** verbindliche Gift-Farbe (docs/12 §2) — nur fuer Glows/Runen, nie fuer den Koerper */
const POISON = KEYWORD_COLORS.poison

export const paintOkra: SpeciesPainter = (p: PaintParams) => {
  const { ctx, x, y, r, colors, tier, pose } = p
  const dead = p.emotion === 'dead'
  // Material-Ton fuer Knochen/Zaehne (wie Metall-Graus beim Kohlrabi); dead => entsaettigt
  const boneCol = dead ? '#b8b5ac' : '#efe6cf'
  const outlineW = Math.max(2, r * 0.12)

  ctx.save()
  ctx.lineJoin = 'round'
  ctx.translate(x, y)
  ctx.rotate(0.09) // leichte Schieflage — der Schamane steht nie ganz gerade

  // --- Schoten-Silhouette: leicht asymmetrisch gebogen = "verdreht" ---
  const podPath = (): void => {
    ctx.beginPath()
    ctx.moveTo(-r * 0.1, -r * 1.08)
    ctx.bezierCurveTo(r * 0.34, -r * 1.0, r * 0.63, -r * 0.52, r * 0.6, r * 0.04)
    ctx.bezierCurveTo(r * 0.57, r * 0.58, r * 0.34, r * 0.9, r * 0.02, r * 0.97)
    ctx.bezierCurveTo(-r * 0.32, r * 0.9, -r * 0.54, r * 0.56, -r * 0.56, r * 0.02)
    ctx.bezierCurveTo(-r * 0.58, -r * 0.6, -r * 0.4, -r * 0.98, -r * 0.1, -r * 1.08)
    ctx.closePath()
  }

  ctx.strokeStyle = colors.outline
  ctx.lineWidth = outlineW
  podPath()
  ctx.fillStyle = volumeGradient(ctx, 0, -r * 0.05, r, colors.body)
  ctx.fill()
  ctx.stroke()

  // --- Innenleben, geclippt auf die Schote ---
  ctx.save()
  podPath()
  ctx.clip()

  // Rippen als 3D-Waelle: dunkle Furche rechts-unten + Licht-Kante zur Lichtquelle
  // (oben-links); Rippen konvergieren oben nach links = Dreh-Eindruck der Schote
  const ribPath = (t: number, ox: number, oy: number): void => {
    ctx.beginPath()
    ctx.moveTo((-0.07 + t * 0.13) * r + ox, (-0.9 + Math.abs(t) * 0.1) * r + oy)
    ctx.quadraticCurveTo(
      t * 0.8 * r + ox, -r * 0.02 + oy,
      (t * 0.24 + 0.04) * r + ox, (0.88 - Math.abs(t) * 0.1) * r + oy,
    )
  }
  ctx.lineCap = 'round'
  for (const t of [-1, -0.35, 0.35, 1]) {
    ctx.strokeStyle = rgba(darken(colors.body, 26), 0.8)
    ctx.lineWidth = Math.max(1.2, r * 0.075)
    ribPath(t, r * 0.02, r * 0.015)
    ctx.stroke()
    ctx.strokeStyle = rgba(lighten(colors.body, 24), dead ? 0.35 : 0.75)
    ctx.lineWidth = Math.max(1, r * 0.045)
    ribPath(t, -r * 0.03, -r * 0.025)
    ctx.stroke()
  }

  // subtiler Gift-Schimmer im Bauch (Keyword-Gruen), pulsiert beim Cast
  if (!dead) {
    glow(ctx, r * 0.04, r * 0.6, r * 0.62, POISON, 0.14 + pose.castPulse * 0.18 + (tier >= 3 ? 0.08 : 0))
  }

  // 2★: Tribal-Wickelband mit Kreuz-Stichen + Zahn-Trophaeen (mehr Ruestungs-Detail)
  if (tier >= 2 && !dead) {
    const bandY = r * 0.56
    const bg = ctx.createLinearGradient(0, bandY - r * 0.13, 0, bandY + r * 0.13)
    bg.addColorStop(0, lighten(colors.accent, 10))
    bg.addColorStop(0.5, darken(colors.accent, 6))
    bg.addColorStop(1, darken(colors.accent, 26))
    ctx.fillStyle = bg
    ctx.strokeStyle = colors.outline
    ctx.lineWidth = Math.max(1.5, r * 0.06)
    ctx.beginPath()
    ctx.ellipse(r * 0.02, bandY, r * 0.64, r * 0.14, -0.06, 0, Math.PI * 2)
    ctx.fill()
    ctx.stroke()
    // Kreuz-Stiche (gewebtes Band)
    ctx.strokeStyle = rgba(lighten(colors.accent, 42), 0.85)
    ctx.lineWidth = Math.max(1, r * 0.035)
    for (const t of [-0.42, -0.14, 0.14, 0.42]) {
      ctx.beginPath()
      ctx.moveTo(t * r - r * 0.04, bandY - r * 0.1)
      ctx.lineTo(t * r + r * 0.04, bandY - r * 0.01)
      ctx.moveTo(t * r + r * 0.04, bandY - r * 0.1)
      ctx.lineTo(t * r - r * 0.04, bandY - r * 0.01)
      ctx.stroke()
    }
    // haengende Zahn-Trophaeen
    ctx.fillStyle = boneCol
    ctx.strokeStyle = colors.outline
    ctx.lineWidth = Math.max(1, r * 0.035)
    for (const t of [-0.24, 0, 0.24]) {
      ctx.beginPath()
      ctx.moveTo(t * r - r * 0.05, bandY + r * 0.1)
      ctx.lineTo(t * r + r * 0.05, bandY + r * 0.1)
      ctx.lineTo(t * r, bandY + r * 0.27)
      ctx.closePath()
      ctx.fill()
      ctx.stroke()
    }
  }
  ctx.restore() // Clip Ende

  // Outline nach dem Innenleben knackig nachziehen (Readability der Silhouette)
  ctx.strokeStyle = colors.outline
  ctx.lineWidth = outlineW
  podPath()
  ctx.stroke()

  // 3★: pulsierende Gift-Runen — drittes Auge auf der Stirn + Tribal-Glyphen seitlich.
  // Jede Rune bekommt eine dunkle Unterlage, damit das Lime auch auf gruenem Body knallt.
  if (tier >= 3 && !dead) {
    const pulse = 0.85 + pose.castPulse * 0.15
    // Glyphen-Pfade einmal definieren, dann in 3 Paessen zeichnen:
    // dunkler Sockel -> Lime-Glow -> heller Kern (so knallt Lime auch auf gruenem Body)
    const eyePath = (): void => {
      ctx.beginPath()
      ctx.moveTo(-r * 0.15, -r * 0.6)
      ctx.quadraticCurveTo(0, -r * 0.73, r * 0.15, -r * 0.6)
      ctx.quadraticCurveTo(0, -r * 0.47, -r * 0.15, -r * 0.6)
      ctx.closePath()
    }
    const spiralPath = (): void => {
      ctx.beginPath()
      ctx.arc(-r * 0.38, r * 0.18, r * 0.09, -0.6, Math.PI * 1.25)
      ctx.moveTo(-r * 0.34, r * 0.18)
      ctx.arc(-r * 0.38, r * 0.18, r * 0.04, Math.PI * 0.6, Math.PI * 2.1)
    }
    const zigzagPath = (): void => {
      ctx.beginPath()
      ctx.moveTo(r * 0.32, r * 0.02)
      ctx.lineTo(r * 0.45, r * 0.1)
      ctx.lineTo(r * 0.33, r * 0.18)
      ctx.lineTo(r * 0.46, r * 0.26)
    }
    ctx.save()
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    // Pass 1: dunkle Augenhoehle + Glyphen-Sockel
    ctx.fillStyle = rgba(darken(colors.body, 38), 0.85)
    ctx.beginPath()
    ctx.ellipse(0, -r * 0.6, r * 0.2, r * 0.14, 0, 0, Math.PI * 2)
    ctx.fill()
    ctx.strokeStyle = rgba(darken(colors.body, 38), 0.85)
    ctx.lineWidth = Math.max(3, r * 0.13)
    spiralPath()
    ctx.stroke()
    zigzagPath()
    ctx.stroke()
    // Pass 2: Lime-Glow
    ctx.strokeStyle = rgba(POISON, pulse)
    ctx.fillStyle = rgba(POISON, pulse)
    ctx.lineWidth = Math.max(1.8, r * 0.065)
    ctx.shadowColor = POISON
    ctx.shadowBlur = r * (0.2 + pose.castPulse * 0.18)
    eyePath()
    ctx.stroke()
    ctx.beginPath()
    ctx.arc(0, -r * 0.6, r * 0.055, 0, Math.PI * 2)
    ctx.fill()
    spiralPath()
    ctx.stroke()
    zigzagPath()
    ctx.stroke()
    // Pass 3: heller Kern (Luminanz-Kontrast wie Kohlrabi-Gold)
    ctx.shadowBlur = 0
    ctx.strokeStyle = rgba(lighten(POISON, 28), pulse)
    ctx.lineWidth = Math.max(1, r * 0.03)
    spiralPath()
    ctx.stroke()
    zigzagPath()
    ctx.stroke()
    ctx.restore()
  }

  // Gift-Tropfen am Schotenende (dezent, waechst mit Sternstufe, dehnt sich beim Cast)
  if (!dead) {
    const dripS = 1 + (tier - 1) * 0.25 + pose.castPulse * 0.4
    const dripY = r * (1.04 + pose.castPulse * 0.07)
    ctx.save()
    ctx.fillStyle = rgba(POISON, 0.85)
    ctx.shadowColor = POISON
    ctx.shadowBlur = r * 0.14
    ctx.beginPath()
    ctx.moveTo(r * 0.04, dripY - r * 0.1 * dripS)
    ctx.quadraticCurveTo(r * (0.04 + 0.09 * dripS), dripY + r * 0.03, r * 0.04, dripY + r * 0.1 * dripS)
    ctx.quadraticCurveTo(r * (0.04 - 0.09 * dripS), dripY + r * 0.03, r * 0.04, dripY - r * 0.1 * dripS)
    ctx.closePath()
    ctx.fill()
    ctx.restore()
  }

  // --- Voodoo-Kappe: Kelch-Kragen mit Sepal-Zacken + verdrehter Stiel-Haken ---
  const cx = -r * 0.08
  const cy = -r * 1.0
  ctx.save()
  ctx.translate(cx, cy)
  ctx.rotate(-0.12)
  // Stiel-Haken (hinter dem Kragen): Outline-Unterlage -> Stiel -> Licht-Kante
  ctx.lineCap = 'round'
  ctx.strokeStyle = colors.outline
  ctx.lineWidth = Math.max(2.5, r * 0.19)
  ctx.beginPath()
  ctx.moveTo(r * 0.02, -r * 0.08)
  ctx.quadraticCurveTo(r * 0.1, -r * 0.4, -r * 0.2, -r * 0.42)
  ctx.stroke()
  ctx.strokeStyle = darken(colors.accent, 4)
  ctx.lineWidth = Math.max(1.8, r * 0.13)
  ctx.stroke()
  ctx.strokeStyle = rgba(lighten(colors.accent, 34), dead ? 0.3 : 0.85)
  ctx.lineWidth = Math.max(1, r * 0.045)
  ctx.beginPath()
  ctx.moveTo(r * 0.02, -r * 0.13)
  ctx.quadraticCurveTo(r * 0.07, -r * 0.35, -r * 0.15, -r * 0.37)
  ctx.stroke()
  // Kelch-Kragen mit drei haengenden Sepal-Spitzen (Mitte kurz — Stirn bleibt frei)
  const cg = ctx.createLinearGradient(0, -r * 0.18, 0, r * 0.26)
  cg.addColorStop(0, lighten(colors.accent, 30))
  cg.addColorStop(0.45, colors.accent)
  cg.addColorStop(1, darken(colors.accent, 22))
  ctx.fillStyle = cg
  ctx.strokeStyle = colors.outline
  ctx.lineWidth = Math.max(1.5, r * 0.07)
  ctx.beginPath()
  ctx.moveTo(-r * 0.34, -r * 0.02)
  ctx.quadraticCurveTo(0, -r * 0.26, r * 0.34, -r * 0.02)
  ctx.lineTo(r * 0.23, r * 0.2)
  ctx.lineTo(r * 0.12, r * 0.05)
  ctx.lineTo(0, r * 0.16)
  ctx.lineTo(-r * 0.12, r * 0.05)
  ctx.lineTo(-r * 0.23, r * 0.2)
  ctx.closePath()
  ctx.fill()
  ctx.stroke()
  // Licht-Kante am Kragen (Lichtquelle oben-links)
  if (!dead) {
    ctx.strokeStyle = rgba(lighten(colors.accent, 44), 0.7)
    ctx.lineWidth = Math.max(1, r * 0.035)
    ctx.beginPath()
    ctx.moveTo(-r * 0.28, -r * 0.05)
    ctx.quadraticCurveTo(-r * 0.08, -r * 0.2, r * 0.14, -r * 0.16)
    ctx.stroke()
  }
  ctx.restore()

  // --- Voodoo-Deko an der Kappe: Knochen links, Feder(n) rechts oben-aussen ---
  // (Federn brechen die Silhouette gegen den dunklen Board-Hintergrund — nicht auf dem Body)
  const cord = (x1: number, y1: number, x2: number, y2: number): void => {
    ctx.strokeStyle = rgba(colors.outline, 0.9)
    ctx.lineWidth = Math.max(1, r * 0.03)
    ctx.beginPath()
    ctx.moveTo(x1, y1)
    ctx.quadraticCurveTo((x1 + x2) / 2 - r * 0.03, (y1 + y2) / 2, x2, y2)
    ctx.stroke()
  }
  const bone = (bx: number, by: number, ang: number, len: number): void => {
    ctx.save()
    ctx.translate(bx, by)
    ctx.rotate(ang)
    ctx.lineCap = 'round'
    // Outline-Unterlage (Schaft + Knubbel)
    ctx.strokeStyle = colors.outline
    ctx.lineWidth = len * 0.42
    ctx.beginPath()
    ctx.moveTo(-len * 0.5, 0)
    ctx.lineTo(len * 0.5, 0)
    ctx.stroke()
    ctx.fillStyle = colors.outline
    for (const e of [-1, 1]) for (const k of [-1, 1]) {
      ctx.beginPath()
      ctx.arc(e * len * 0.48, k * len * 0.13, len * 0.22, 0, Math.PI * 2)
      ctx.fill()
    }
    // Elfenbein obendrauf
    ctx.fillStyle = boneCol
    for (const e of [-1, 1]) for (const k of [-1, 1]) {
      ctx.beginPath()
      ctx.arc(e * len * 0.48, k * len * 0.13, len * 0.16, 0, Math.PI * 2)
      ctx.fill()
    }
    ctx.strokeStyle = boneCol
    ctx.lineWidth = len * 0.26
    ctx.beginPath()
    ctx.moveTo(-len * 0.42, 0)
    ctx.lineTo(len * 0.42, 0)
    ctx.stroke()
    ctx.restore()
  }
  const feather = (fx: number, fy: number, ang: number, len: number, col: string): void => {
    ctx.save()
    ctx.translate(fx, fy)
    ctx.rotate(ang)
    const fg = ctx.createLinearGradient(0, -len * 0.5, 0, len * 0.5)
    fg.addColorStop(0, lighten(col, 20))
    fg.addColorStop(1, darken(col, 22))
    ctx.fillStyle = fg
    ctx.strokeStyle = colors.outline
    ctx.lineWidth = Math.max(1, r * 0.04)
    ctx.beginPath()
    ctx.moveTo(0, -len * 0.5)
    ctx.quadraticCurveTo(len * 0.32, -len * 0.08, 0, len * 0.5)
    ctx.quadraticCurveTo(-len * 0.32, -len * 0.08, 0, -len * 0.5)
    ctx.closePath()
    ctx.fill()
    ctx.stroke()
    // Feder-Kiel
    ctx.strokeStyle = rgba(lighten(col, 36), 0.9)
    ctx.lineWidth = Math.max(1, r * 0.028)
    ctx.beginPath()
    ctx.moveTo(0, -len * 0.34)
    ctx.lineTo(0, len * 0.38)
    ctx.stroke()
    ctx.restore()
  }
  cord(-r * 0.38, -r * 0.94, -r * 0.5, -r * 0.76)
  bone(-r * 0.52, -r * 0.72, 0.55, r * 0.32)
  // Hutfedern: an der Kappe verankert, fachern nach oben-rechts aus der Silhouette
  if (tier >= 2) {
    feather(r * 0.45, -r * 0.94, -1.15, r * 0.32, colors.bodyDark)
  }
  feather(r * 0.4, -r * 1.04, -0.8, r * 0.42, lighten(colors.accent, 22))

  // --- Licht: Rim oben-links + Specular (nie auf Toten) ---
  if (!dead) {
    rimLight(ctx, 0, -r * 0.06, r * 0.62, r * 1.0, 'rgba(250, 255, 235, 0.55)', Math.max(1.5, r * 0.07))
    specular(ctx, -r * 0.2, -r * 0.52, r * 0.13, 0.45)
  }

  ctx.restore()
}
